import { ChevronRight } from "lucide-react";
import { Fragment, type ReactNode, useMemo, useState } from "react";
import { splitItem } from "./release-item";

/**
 * A very small markdown renderer for release-note bodies. The app intentionally
 * has no markdown-parser dependency (see `HelpContent`), and release bodies use
 * only a narrow subset — `##`/`###` headings, `-`/`*` bullet lists (with lines
 * that wrap onto the next physical line), `**bold**`, and plain paragraphs.
 * Anything fancier degrades gracefully to text. Output is React text nodes, so
 * it is XSS-safe by construction (no `dangerouslySetInnerHTML`).
 *
 * Blocks and list items carry a stable `id` assigned at parse time so React
 * keys never depend on the render index (the changelog legitimately repeats
 * headings like "### Fixed", so content alone isn't unique).
 */

type Block =
  | { id: number; kind: "heading"; level: number; text: string }
  | { id: number; kind: "list"; items: { id: number; text: string }[] }
  | { id: number; kind: "para"; text: string };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^[-*]\s+(.*)$/;

function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let para: string[] = [];
  let nextId = 0;

  const flushPara = () => {
    if (para.length > 0) {
      blocks.push({ id: nextId++, kind: "para", text: para.join(" ") });
      para = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = (lines[i] ?? "").trim();

    if (line === "") {
      flushPara();
      i += 1;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading?.[2] !== undefined) {
      flushPara();
      blocks.push({
        id: nextId++,
        kind: "heading",
        level: heading[1]?.length ?? 3,
        text: heading[2],
      });
      i += 1;
      continue;
    }

    if (BULLET.test(line)) {
      flushPara();
      const items: { id: number; text: string }[] = [];
      while (i < lines.length) {
        const t = (lines[i] ?? "").trim();
        if (t === "") {
          i += 1;
          break;
        }
        const m = BULLET.exec(t);
        if (m?.[1] !== undefined) {
          items.push({ id: nextId++, text: m[1] });
          i += 1;
        } else if (HEADING.test(t)) {
          break;
        } else if (items.length > 0) {
          // Continuation of the previous bullet wrapped onto a new line.
          const last = items[items.length - 1];
          if (last) last.text = `${last.text} ${t}`;
          i += 1;
        } else {
          break;
        }
      }
      blocks.push({ id: nextId++, kind: "list", items });
      continue;
    }

    para.push(line);
    i += 1;
  }
  flushPara();
  return blocks;
}

/** Split a run of text on `**bold**` markers into text + <strong> nodes. */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let offset = 0;
  for (const part of text.split(/(\*\*[^*]+\*\*)/g)) {
    // Key on the character offset — unique per part, and not the render index.
    const key = `k${offset}`;
    offset += part.length;
    if (part === "") continue;
    const bold = /^\*\*([^*]+)\*\*$/.exec(part);
    nodes.push(
      bold?.[1] !== undefined ? (
        <strong key={key} className="font-semibold text-foreground">
          {bold[1]}
        </strong>
      ) : (
        <Fragment key={key}>{part}</Fragment>
      ),
    );
  }
  return nodes;
}

/**
 * One change: the headline you scan, and the explanation you open.
 *
 * A `<details>` so it is keyboard-operable and expandable with no JavaScript,
 * matching the release wrapper on the page around it. A bullet with nothing to
 * expand renders as a plain row — an arrow that opens an empty box is worse
 * than no arrow.
 */
function ReleaseChange({ text, expandAll }: { text: string; expandAll: boolean }) {
  const { headline, detail } = useMemo(() => splitItem(text), [text]);

  if (detail === null) {
    return (
      <li className="flex gap-2 py-1 leading-relaxed">
        <span aria-hidden="true" className="mt-2 size-1 shrink-0 rounded-full bg-border" />
        <span className="min-w-0">{renderInline(headline)}</span>
      </li>
    );
  }

  return (
    <li>
      {/* `key` on expandAll so flipping it re-mounts every row at the new
          default — otherwise `open` is uncontrolled and rows the reader has
          already toggled would ignore Expand all. */}
      {/* NAMED group. `group-open:` compiles to `.group[open] &`, which matches
          ANY group ancestor — and this row renders inside the release's own
          `<details class="group">`, which is open whenever you can see the row
          at all. Unnamed, every chevron here sat permanently rotated and the
          arrow told you nothing. */}
      <details
        key={String(expandAll)}
        open={expandAll}
        className="group/change [&_summary::-webkit-details-marker]:hidden"
      >
        <summary className="-mx-1.5 flex cursor-pointer list-none items-start gap-2 rounded px-1.5 py-1 leading-relaxed hover:bg-muted/60">
          <ChevronRight
            aria-hidden="true"
            className="mt-1 size-3.5 shrink-0 text-muted-foreground transition-transform duration-150 group-open/change:rotate-90"
          />
          <span className="min-w-0 font-medium text-foreground">{renderInline(headline)}</span>
        </summary>
        <div className="ml-[1.375rem] pb-2 pr-1 pt-0.5 leading-relaxed text-foreground/80">
          {renderInline(detail)}
        </div>
      </details>
    </li>
  );
}

/**
 * A parsed body. `splitChanges` decides whether list items are treated as
 * changes (headline + expandable detail) or rendered whole — the technical
 * notes are already one line each, so splitting them would invent a second
 * tier that isn't there.
 */
function Blocks({
  source,
  splitChanges,
  expandAll,
}: {
  source: string;
  splitChanges: boolean;
  expandAll: boolean;
}) {
  const blocks = useMemo(() => parseBlocks(source), [source]);

  return (
    <div className="space-y-3 text-sm text-foreground/90">
      {blocks.map((block) => {
        if (block.kind === "heading") {
          return (
            <p
              key={block.id}
              className="pt-1 text-2xs font-bold uppercase tracking-[0.12em] text-muted-foreground"
            >
              {block.text}
            </p>
          );
        }
        if (block.kind === "list") {
          return (
            <ul key={block.id} className={splitChanges ? "space-y-0.5" : "space-y-1.5"}>
              {block.items.map((item) =>
                splitChanges ? (
                  <ReleaseChange key={item.id} text={item.text} expandAll={expandAll} />
                ) : (
                  <li key={item.id} className="flex gap-2 leading-relaxed">
                    <span
                      aria-hidden="true"
                      className="mt-2 size-1 shrink-0 rounded-full bg-border"
                    />
                    <span className="min-w-0">{renderInline(item.text)}</span>
                  </li>
                ),
              )}
            </ul>
          );
        }
        return (
          <p key={block.id} className="leading-relaxed">
            {renderInline(block.text)}
          </p>
        );
      })}
    </div>
  );
}

type View = "summary" | "detail" | "technical";

/**
 * One release's notes, at the depth the reader asks for.
 *
 * Summary and Detail are the same text at two depths — the headline of each
 * change, or the headline with its explanation. Technical is a different text
 * entirely: the commits, generated into `CHANGELOG.technical.md` and seeded
 * alongside the notes. Its tab is hidden when a release has none, which is the
 * case for the releases inherited from the template: their commits predate this
 * repository, so there is nothing honest to show.
 */
export function ReleaseBody({ source, technical }: { source: string; technical?: string | null }) {
  const [view, setView] = useState<View>("summary");
  const hasTechnical = (technical ?? "").trim() !== "";

  const tabs: { key: View; label: string }[] = [
    { key: "summary", label: "Summary" },
    { key: "detail", label: "In full" },
    ...(hasTechnical ? [{ key: "technical" as const, label: "Technical" }] : []),
  ];

  return (
    <div className="space-y-3">
      <div role="tablist" aria-label="How much detail to show" className="flex gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={view === tab.key}
            onClick={() => setView(tab.key)}
            className={
              view === tab.key
                ? "rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
                : "rounded-full px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      {view === "technical" ? (
        <Blocks source={technical ?? ""} splitChanges={false} expandAll={false} />
      ) : (
        <Blocks source={source} splitChanges expandAll={view === "detail"} />
      )}
    </div>
  );
}
